import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useCallback } from "react";
import { UserInterestPayload } from "../../../api/UserInterestApi.ts";

// List of all US states plus IMLC option
const stateOptions = [
  { label: "IMLC licensed", value: "IMLC" },
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
];

// Component props interface
interface StateMedicalLicensureProps {
  onStateLicenseChange: (val: string[]) => void;
  formData: UserInterestPayload;
}

/**
 * StateMedicalLicensure Component
 * Allows users to select multiple state medical licenses
 * Has special handling for IMLC (Interstate Medical Licensure Compact)
 */
const StateMedicalLicensure: React.FC<StateMedicalLicensureProps> = ({
  formData,
  onStateLicenseChange,
}) => {
  // Handle toggling of individual licenses
  const handleLicenseToggle = useCallback(
    (value: string) => {
      const currentLicenses = formData.stateLicenses || [];
      let newLicenses: string[];

      if (currentLicenses.includes(value)) {
        // If IMLC is being removed, clear all licenses
        if (value === "IMLC") {
          newLicenses = [];
        } else {
          // Remove the clicked license
          newLicenses = currentLicenses.filter((license) => license !== value);
        }
      } else {
        // If IMLC is being added, only keep IMLC
        // if (value === "IMLC") {
        //   newLicenses = ["IMLC"];
        // } else {
        //   // Don't allow adding other states if IMLC is selected
        //   if (currentLicenses.includes("IMLC")) {
        //     return;
        //   }
        // Add the new license to existing ones
        newLicenses = [...currentLicenses, value];
        // }
      }

      onStateLicenseChange(newLicenses);
    },
    [formData.stateLicenses, onStateLicenseChange],
  );

  // Get the text to display on the menu button
  const getButtonText = useCallback(() => {
    if (!formData.stateLicenses?.length) {
      return "Select medical licensure";
    }
    return `${formData.stateLicenses.length} state${
      formData.stateLicenses.length > 1 ? "s" : ""
    } selected`;
  }, [formData.stateLicenses]);

  return (
    <FormControl maxW="368" alignSelf="center">
      <FormLabel color="gray.500" fontSize="12px" fontWeight="700">
        State medical licensure
      </FormLabel>

      {/* Dropdown menu for state selection */}
      <Menu closeOnSelect={false}>
        <MenuButton
          as={Button}
          rightIcon={<ChevronDownIcon />}
          variant="outline"
          height={14}
          w="full"
          borderColor="gray.300"
          textAlign="left"
          color={formData.stateLicenses?.length ? "gray.600" : "gray.400"}
          fontWeight="400"
        >
          {getButtonText()}
        </MenuButton>

        {/* List of states to select from */}
        <MenuList as={Box} maxHeight="300px" overflowY="auto">
          {stateOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleLicenseToggle(option.value)}
              _hover={{ bg: "gray.100" }}
            >
              <HStack justify="space-between" width="100%">
                <Checkbox
                  isChecked={formData.stateLicenses?.includes(option.value)}
                  colorScheme="brandBlue"
                  // isDisabled={
                  //   formData.stateLicenses?.includes("IMLC") && option.value !== "IMLC"
                  // }
                  pointerEvents="none"
                />
                <Text flex="1">{option.label}</Text>
              </HStack>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </FormControl>
  );
};

export default StateMedicalLicensure;
